import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Stepper,
  Step,
  StepButton,
  Button,
  Container,
  Snackbar,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { Send as SendIcon } from "@material-ui/icons";

import AssetUploaderManager from "../AssetUploader/UploadStep/AssetUploaderManager";
import AssetOptions from "../AssetUploader/OptionsStep/AssetOptions";
import AssetNaming from "../AssetUploader/NamingStep/AssetNaming";
import AssetReview from "../AssetUploader/ReviewStep/AssetReview";
import CompanionStep from "../AssetUploader/CompanionStep/CompanionStep";
import Confirmation from "../Shared/Confirmation/Confirmation";

import { useSelector } from "react-redux";

import { insertCreatives } from "../../services/dcm/creatives";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%"
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  stepper: {
    margin: "20px auto 30px",
    width: "60%",
    paddingTop: 0
  }
}));

const next = {
  float: "right"
};

// A companion can only be attached while the media creative is being created:
// CM360 stores the link on the creative itself (companionCreatives), and
// BartAPI sets it only inside insertMediaCreativeWithCompanion at insert time.
// There is no update path that adds a companion to a creative already in GCM,
// which is what CompanionCreative.isUploadDisabled()'s dcmId check enforces.
// So only offer the opt-in when there's a freshly uploaded primary to attach
// one to — otherwise the companion step is a dead end with a disabled button.
function canAttachCompanion(creatives) {
  return creatives.some(
    (c) =>
      (c.creativeRole || "PRIMARY") === "PRIMARY" && !c.dcmId && !c.dcmCreative
  );
}

function getSteps(hasCompanion, allExisting) {
  let nameStepLabel = allExisting ? "Review URL(s)" : "Generate name(s)";
  if (hasCompanion) {
    return [
      "Upload audio(s)",
      "Add companion creative(s)",
      nameStepLabel,
      "Select options",
      "Review"
    ];
  }
  return ["Upload audio(s)", nameStepLabel, "Select options", "Review"];
}

const creativeTypes = ["INSTREAM_AUDIO"];

function getStepContent(stepIndex, props) {
  const { hasCompanion } = props;

  // Map step index to content based on whether companion step is active
  if (hasCompanion) {
    switch (stepIndex) {
      case 0:
        return (
          <div>
            <AssetUploaderManager
              creativeTypes={creativeTypes}
              isThumbnailEnabled={false}
              selectedCreatives={props.selectedCreatives}
              setSelectedCreatives={props.setSelectedCreatives}
              placements={props.placements}
              filetype={"audio/*"}
              ref={(r) => (props.stepRef.current = r)}
              headerExtra={
                props.canHaveCompanion ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hasCompanion}
                        onChange={(e) => props.setHasCompanion(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Add companion creatives (image/HTML5 displayed around the audio player)"
                    style={{ marginBottom: 8 }}
                  />
                ) : null
              }
            />
          </div>
        );
      case 1:
        return (
          <div>
            <CompanionStep
              isClickEnabled={true}
              isDragDisabled={true}
              selectedCreatives={props.selectedCreatives}
              setSelectedCreatives={props.setSelectedCreatives}
              isThumbnailEnabled={false}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      case 2:
        return (
          <div>
            <AssetNaming
              isClickEnabled={true}
              isDragDisabled={true}
              isThumbnailEnabled={false}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      case 3:
        return (
          <div>
            <AssetOptions
              isClickEnabled={false}
              isDragDisabled={false}
              isThumbnailEnabled={false}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      case 4:
        return (
          <div>
            <AssetReview
              isClickEnabled={false}
              isDragDisabled={true}
              isThumbnailEnabled={false}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      default:
        return " ";
    }
  } else {
    switch (stepIndex) {
      case 0:
        return (
          <div>
            <AssetUploaderManager
              creativeTypes={creativeTypes}
              isThumbnailEnabled={false}
              selectedCreatives={props.selectedCreatives}
              setSelectedCreatives={props.setSelectedCreatives}
              placements={props.placements}
              filetype={"audio/*"}
              ref={(r) => (props.stepRef.current = r)}
              headerExtra={
                props.canHaveCompanion ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hasCompanion}
                        onChange={(e) => props.setHasCompanion(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Add companion creatives (image/HTML5 displayed around the audio player)"
                    style={{ marginBottom: 8 }}
                  />
                ) : null
              }
            />
          </div>
        );
      case 1:
        return (
          <div>
            <AssetNaming
              isClickEnabled={true}
              isDragDisabled={true}
              isThumbnailEnabled={false}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      case 2:
        return (
          <div>
            <AssetOptions
              isClickEnabled={false}
              isDragDisabled={false}
              isThumbnailEnabled={false}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      case 3:
        return (
          <div>
            <AssetReview
              isClickEnabled={false}
              isDragDisabled={true}
              isThumbnailEnabled={false}
              ref={(r) => (props.stepRef.current = r)}
            />
          </div>
        );
      default:
        return " ";
    }
  }
}

export default () => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = React.useState(0);
  // Tracks an actual submission. Deriving "done" from
  // activeStep === steps.length is unsafe when the step count is derived
  // from creative state: if a step disappears while the user sits on a
  // later one, activeStep can land on steps.length and report a
  // successful submission that never happened.
  const [hasSubmitted, setHasSubmitted] = React.useState(false);
  const [completed, setCompleted] = React.useState(new Set());
  const [skipped, setSkipped] = React.useState(new Set());
  const [hasCompanion, setHasCompanion] = useState(false);
  const creativesState = useSelector((state) => state.creatives);
  let creativesArr = Object.values(creativesState.byId);
  let allExisting = creativesArr.length > 0 && creativesArr.every((c) => c.dcmCreative);
  const canHaveCompanion = canAttachCompanion(creativesArr);
  // Ignore a stale opt-in once nothing in the batch can take a companion (e.g.
  // the fresh upload it was ticked for was swapped for an existing creative),
  // so the step drops out instead of lingering with no way to untick it.
  const companionActive = hasCompanion && canHaveCompanion;
  const steps = getSteps(companionActive, allExisting);

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedCreatives, setSelectedCreatives] = React.useState([]);

  const stepRef = useRef();

  const history = useHistory();

  useEffect(() => {
    if (creativesState.assignedPlacements.length < 1) {
      history.push("/");
    }
  }, [creativesState.assignedPlacements.length, history]);

  // The companion step can now disappear while the user is on a later step —
  // removing the last freshly uploaded creative drops it — so pull activeStep
  // back into range instead of rendering a blank out-of-range step. Skipped
  // once submitted, when activeStep intentionally sits at steps.length.
  useEffect(() => {
    if (hasSubmitted) return;
    if (activeStep > steps.length - 1) {
      setActiveStep(Math.max(0, steps.length - 1));
    }
  }, [activeStep, steps.length, hasSubmitted]);

  const handleNext = () => {
    if (
      stepRef &&
      stepRef.current &&
      stepRef.current.hasOwnProperty("validate")
    ) {
      const validation = stepRef.current.validate();

      if (validation.hasErrors) {
        if (validation.message) {
          setSnackbarMessage(validation.message);
          setSnackbarOpen(true);
        }

        return;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setHasSubmitted(false);
    setActiveStep(0);
  };

  const handleStep = (step) => () => {
    // The Stepper header stays mounted on the Confirmation screen, so a
    // StepButton click has to drop out of it — otherwise activeStep moves
    // but Confirmation keeps rendering and the user is stuck there.
    setHasSubmitted(false);
    setActiveStep(step);
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  function isStepComplete(step) {
    return completed.has(step);
  }

  const handleTraffic = () => {
    insertCreatives(
      Object.values(creativesState.byId),
      creativesState.assignedPlacements,
      "INSTREAM_AUDIO"
    );
    // Set before advancing: the Confirmation screen is gated on this,
    // not on the step index.
    setHasSubmitted(true);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  return (
    <div className={classes.root}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        nonLinear
        className={classes.stepper}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const buttonProps = {};
          if (isStepSkipped(index)) {
            stepProps.completed = false;
          }
          return (
            <Step key={label} {...stepProps}>
              <StepButton
                onClick={handleStep(index)}
                completed={isStepComplete(index)}
                {...buttonProps}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
      <Container maxWidth="lg">
        <div>
          {hasSubmitted ? (
            <Confirmation handleResetParent={handleReset} />
          ) : (
            <div>
              <div style={next}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  className={classes.button}
                  disableElevation
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleTraffic}
                    startIcon={<SendIcon />}
                    disableElevation
                  >
                    Submit Placements
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    disableElevation
                  >
                    Next
                  </Button>
                )}
              </div>
              {getStepContent(activeStep, {
                stepRef,
                selectedCreatives,
                setSelectedCreatives,
                placements: creativesState.assignedPlacements,
                // companionActive, not raw hasCompanion — getStepContent's
                // step-index mapping has to agree with getSteps above.
                hasCompanion: companionActive,
                setHasCompanion,
                canHaveCompanion
              })}
            </div>
          )}
        </div>
      </Container>
      <Snackbar
        autoHideDuration={3000}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity="warning">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
